/* slipOdds — pure helpers for the bet slip's live price-change handling.
 *
 * These encode the ONE rule that keeps auto-accept safe: a PRICE-only move (same proposition / same
 * line) may be auto-accepted in either direction; a LINE change (spread/total/prop threshold moved) is
 * a DIFFERENT proposition and must be explicitly re-selected, never silently booked. Kept framework-
 * free so they're unit-tested directly and shared by GlobalBetSlip (desktop rail + mobile sheet).
 *
 * A "leg" is the stored slip pick: { odds, marketVersion, line }.
 * A "quote" is the authoritative /odds/quote row: { odds, available, marketVersion, line }.
 */

// Two lines are the SAME proposition only when their point/threshold matches (moneyline = null both).
export function sameLine(a, b) {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  const x = Number(a), y = Number(b);
  if (Number.isNaN(x) || Number.isNaN(y)) return String(a) === String(b);
  return Math.abs(x - y) <= 1e-9;
}

// Classify a leg against its current authoritative quote.
//  - unavail:     the market is gone / has no price -> blocks placement
//  - lineChanged: a different proposition (line moved) -> needs explicit re-selection, never auto-accept
//  - priceOnly:   same proposition, only the price/version moved -> auto-acceptable when opted in
export function classifyLeg(leg, quote) {
  if (!quote) return { q: null, unavail: false, priceOnly: false, lineChanged: false };
  const unavail = quote.available === false || quote.odds == null;
  const lineChanged = !unavail && !sameLine(leg.line, quote.line);
  const priceMoved = quote.odds != null && Math.abs(quote.odds - leg.odds) > 1e-9;
  const versionMoved = quote.marketVersion != null && Number(quote.marketVersion) !== Number(leg.marketVersion || 0);
  const priceOnly = !unavail && !lineChanged && (priceMoved || versionMoved);
  return { q: quote, unavail, priceOnly, lineChanged };
}

// The price to VALUE a leg at for display/payout. With auto-accept ON, a price-only-moved leg is valued
// at the CURRENT price (the one about to be booked) so the shown payout matches the booked price;
// otherwise the locked leg price. A line-changed or unavailable leg keeps its stored odds (placement is
// blocked for it anyway, so the payout never implies a price we won't actually take).
export function workingOdds(leg, quote, acceptChanges) {
  const s = classifyLeg(leg, quote);
  if (acceptChanges && s.priceOnly && s.q && s.q.odds != null) {
    return Number(s.q.odds) || Number(leg.odds) || 1;
  }
  return Number(leg.odds) || 1;
}
